import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FileText, Download, Mail, Phone, MapPin, Calendar, Clock, ChevronDown, ChevronUp, Loader2, RefreshCw, LogOut, ClipboardList, FileCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import TenderReviewDashboard from "../components/TenderReviewDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [activeTab, setActiveTab] = useState("quotes"); // "quotes" or "tenders"
  const [token, setToken] = useState(null);

  const scrollToContact = () => {
    navigate("/#contact");
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("blackline_token");
      if (!storedToken) {
        navigate("/login");
        return;
      }

      try {
        await axios.get(`${API}/auth/verify`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setToken(storedToken);
        setAuthenticated(true);
        setCheckingAuth(false);
      } catch (err) {
        console.error("Auth check failed:", err);
        localStorage.removeItem("blackline_token");
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate]);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    const storedToken = localStorage.getItem("blackline_token");
    
    try {
      const response = await axios.get(`${API}/quote-requests`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      // Sort by date, newest first
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setRequests(sorted);
    } catch (err) {
      console.error("Error fetching requests:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("blackline_token");
        navigate("/login");
      } else {
        setError("Failed to load requests. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchRequests();
    }
  }, [authenticated]);

  const handleLogout = () => {
    localStorage.removeItem("blackline_token");
    navigate("/login");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={scrollToContact} />
      
      <main className="pt-20 md:pt-24">
        {/* Header */}
        <section className="py-12 md:py-16 bg-black text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
                  Admin
                </span>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-2">
                  Dashboard
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 hover:text-red-400 hover:border-red-400 transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut size={16} />
                  <span className="font-mono text-sm uppercase tracking-widest">Logout</span>
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-4 mt-8 border-b border-zinc-800">
              <button
                onClick={() => setActiveTab("quotes")}
                className={`flex items-center gap-2 px-4 py-3 font-mono text-sm uppercase tracking-widest transition-colors ${
                  activeTab === "quotes"
                    ? "text-white border-b-2 border-white"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid="tab-quotes"
              >
                <ClipboardList size={18} />
                Quote Requests
                <span className={`ml-2 px-2 py-0.5 text-xs ${activeTab === "quotes" ? "bg-white text-black" : "bg-zinc-800"}`}>
                  {requests.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab("tenders")}
                className={`flex items-center gap-2 px-4 py-3 font-mono text-sm uppercase tracking-widest transition-colors ${
                  activeTab === "tenders"
                    ? "text-white border-b-2 border-white"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid="tab-tenders"
              >
                <FileCheck size={18} />
                Tender Reviews
              </button>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {activeTab === "quotes" ? (
              /* Quote Requests Tab */
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading font-bold text-xl uppercase">
                    Quote Requests ({requests.length})
                  </h2>
                  <button
                    onClick={fetchRequests}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-black hover:bg-zinc-100 transition-colors"
                    data-testid="refresh-quotes"
                  >
                    <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                    <span className="font-mono text-sm uppercase tracking-widest">Refresh</span>
                  </button>
                </div>
                {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 size={32} className="animate-spin text-zinc-400" />
              </div>
            ) : error ? (
              <div className="text-center py-24">
                <p className="font-body text-zinc-600 mb-4">{error}</p>
                <button
                  onClick={fetchRequests}
                  className="font-mono text-sm uppercase tracking-widest text-black hover:underline"
                >
                  Try Again
                </button>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-24 border-2 border-dashed border-zinc-300">
                <p className="font-heading font-bold text-xl uppercase text-zinc-400 mb-2">
                  No Requests Yet
                </p>
                <p className="font-body text-zinc-500">
                  Quote requests will appear here when submitted.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="border-2 border-black bg-white"
                    data-testid={`request-${request.id}`}
                  >
                    {/* Request Header - Always visible */}
                    <button
                      onClick={() => toggleExpand(request.id)}
                      className="w-full p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 text-left hover:bg-zinc-50 transition-colors"
                    >
                      <div className="flex-grow">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <h3 className="font-heading font-bold text-lg uppercase">
                            {request.name}
                          </h3>
                          <span className={`font-mono text-xs px-2 py-1 ${
                            request.service_type === "price_desk" 
                              ? "bg-blue-600 text-white" 
                              : "bg-black text-white"
                          }`}>
                            {request.service_type === "price_desk" ? "PRICE DESK" : request.service_type}
                          </span>
                          {request.timeframe === "ASAP / Rush" && (
                            <span className="font-mono text-xs bg-zinc-200 text-black px-2 py-1">
                              RUSH
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-600">
                          <span className="font-body">{request.company}</span>
                          <span className="text-zinc-300">|</span>
                          <span className="font-body">{request.project_type}</span>
                          <span className="text-zinc-300">|</span>
                          <span className="font-mono text-xs">{request.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-mono text-xs text-zinc-500">
                            {formatDate(request.created_at)}
                          </p>
                          {request.file_paths && request.file_paths.length > 0 && (
                            <p className="font-mono text-xs text-zinc-400 flex items-center gap-1 justify-end mt-1">
                              <FileText size={12} />
                              {request.file_paths.length} file(s)
                            </p>
                          )}
                        </div>
                        {expandedId === request.id ? (
                          <ChevronUp size={20} className="text-zinc-400" />
                        ) : (
                          <ChevronDown size={20} className="text-zinc-400" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {expandedId === request.id && (
                      <div className="border-t-2 border-black p-6 bg-zinc-50">
                        <div className="grid md:grid-cols-2 gap-8">
                          {/* Contact Info */}
                          <div>
                            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
                              Contact Details
                            </h4>
                            <div className="space-y-3">
                              <a
                                href={`mailto:${request.email}`}
                                className="flex items-center gap-3 text-zinc-700 hover:text-black transition-colors"
                              >
                                <Mail size={16} className="flex-shrink-0" />
                                <span className="font-mono text-sm">{request.email}</span>
                              </a>
                              <a
                                href={`tel:${request.phone}`}
                                className="flex items-center gap-3 text-zinc-700 hover:text-black transition-colors"
                              >
                                <Phone size={16} className="flex-shrink-0" />
                                <span className="font-mono text-sm">{request.phone}</span>
                              </a>
                              <div className="flex items-center gap-3 text-zinc-700">
                                <MapPin size={16} className="flex-shrink-0" />
                                <span className="font-body text-sm">{request.location}</span>
                              </div>
                            </div>
                          </div>

                          {/* Project Info */}
                          <div>
                            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
                              Project Details
                            </h4>
                            <div className="space-y-3">
                              <div className="flex items-start gap-3">
                                <Calendar size={16} className="flex-shrink-0 mt-0.5 text-zinc-500" />
                                <div>
                                  <p className="font-mono text-xs text-zinc-500 uppercase">Timeframe</p>
                                  <p className="font-body text-sm">{request.timeframe}</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <Clock size={16} className="flex-shrink-0 mt-0.5 text-zinc-500" />
                                <div>
                                  <p className="font-mono text-xs text-zinc-500 uppercase">Service Required</p>
                                  <p className="font-body text-sm">{request.service_type}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Notes */}
                        {request.notes && (
                          <div className="mt-6 pt-6 border-t border-zinc-200">
                            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-3">
                              Notes
                            </h4>
                            <p className="font-body text-zinc-700 whitespace-pre-wrap">
                              {request.notes}
                            </p>
                          </div>
                        )}

                        {/* Files */}
                        {request.file_paths && request.file_paths.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-zinc-200">
                            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-3">
                              Attached Files
                            </h4>
                            <div className="space-y-2">
                              {request.file_paths.map((filePath, index) => {
                                // Get original filename from uploaded_files or use path
                                const fileName = filePath.split('/').pop();
                                const token = localStorage.getItem("blackline_token");
                                return (
                                  <button
                                    key={index}
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(`${API}/files/${filePath}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                          responseType: 'blob'
                                        });
                                        const url = window.URL.createObjectURL(response.data);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = fileName;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                      } catch (err) {
                                        console.error("Download failed:", err);
                                        if (err.response?.status === 404) {
                                          alert("File not found. This file may have been uploaded before the current storage system was implemented, or it may have been deleted.");
                                        } else if (err.response?.status === 401) {
                                          alert("Session expired. Please log in again.");
                                        } else {
                                          alert("Download failed: " + (err.response?.data?.detail || err.message));
                                        }
                                      }
                                    }}
                                    className="w-full flex items-center gap-3 p-3 border border-zinc-300 bg-white hover:bg-zinc-100 transition-colors text-left"
                                  >
                                    <FileText size={20} className="text-zinc-500" />
                                    <span className="font-mono text-sm flex-grow">{fileName}</span>
                                    <Download size={16} className="text-zinc-400" />
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="mt-6 pt-6 border-t border-zinc-200 flex flex-wrap gap-3">
                          <a
                            href={`mailto:${request.email}?subject=RE: ${request.service_type} Enquiry - ${request.project_type}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white font-mono text-sm uppercase tracking-widest hover:bg-zinc-800 transition-colors"
                          >
                            <Mail size={14} />
                            Reply via Email
                          </a>
                          <a
                            href={`tel:${request.phone}`}
                            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black text-black font-mono text-sm uppercase tracking-widest hover:bg-zinc-100 transition-colors"
                          >
                            <Phone size={14} />
                            Call
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
              </>
            ) : (
              /* Tender Reviews Tab */
              <TenderReviewDashboard token={token} />
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
